import { useMemo } from "react";

import { useTranslate } from "@similarweb/i18n-provider";

import type { TreeSelectSource } from "app/pages/sales-intelligence/common-components/dropdown/TreeSelectCombobox/types";
import type { IntentNodeId } from "app/pages/sales-intelligence/domain/intent/types/taxonomy";
import { indexIntentTaxonomy } from "app/pages/sales-intelligence/domain/intent/utils/taxonomy";
import type { TechnologyNodeId } from "app/pages/sales-intelligence/domain/technology/types/taxonomy";
import { indexTechnologyTaxonomy } from "app/pages/sales-intelligence/domain/technology/utils/taxonomy";
import useIntentTaxonomyDescriptionQuery from "app/pages/sales-intelligence/modules/intent/queries/useIntentTaxonomyDescriptionQuery";
import useTechnologiesDescriptionQuery from "app/pages/sales-intelligence/modules/technology/queries/useTechnologiesDescriptionQuery";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";

import type { SignalEnrichmentFiltersPayload } from "../types/model";
import { createSignalTreeSource } from "../view/signal-tree/factory";
import { createSubstringTaxonomySearch } from "../view/signal-tree/search";

import { useDealSignalFilterSelection } from "./useDealSignalFilterSelection";

type DealSignalTaxonomyFilterCategory<TId extends string> = {
    readonly isPending: boolean;
    readonly isError: boolean;
    readonly refetch: VoidFunction;
    readonly viewModel?: TreeSelectSource<TId>;
    readonly selectedIds: readonly TId[];
    readonly onChange: (next: readonly TId[]) => void;
};

type DealSignalTaxonomyFilters = {
    readonly isIntentFilterVisible: boolean;
    readonly intent: DealSignalTaxonomyFilterCategory<IntentNodeId>;
    readonly technology: DealSignalTaxonomyFilterCategory<TechnologyNodeId>;
    readonly buildPayload: () => SignalEnrichmentFiltersPayload | undefined;
};

/**
 * The two signal taxonomies a deal-signal enrichment can be narrowed by, from query to tree view model,
 * with the selection they are rendered against.
 *
 * The view models close over the selection, so splitting the two apart would only push the memo chain
 * back into the caller. `buildPayload` keeps the taxonomy indexes in here for the same reason: they
 * exist solely to turn picked node ids into the request shape.
 */
export const useDealSignalTaxonomyFilters = (): DealSignalTaxonomyFilters => {
    const { getLocale } = useTranslate();
    const locale = getLocale();

    const { intentSelectedIds, technologySelectedIds, setIntentSelectedIds, setTechnologySelectedIds, buildPayload } =
        useDealSignalFilterSelection();

    const isIntentFilterVisible = salesSettingsHelper.isIntentSignalsEnabled();

    const {
        data: intentTaxonomy,
        isPending: isIntentTaxonomyPending,
        isError: isIntentTaxonomyError,
        refetch: refetchIntentTaxonomy,
    } = useIntentTaxonomyDescriptionQuery({ enabled: isIntentFilterVisible });

    const {
        data: technologyTaxonomy,
        isPending: isTechnologyTaxonomyPending,
        isError: isTechnologyTaxonomyError,
        refetch: refetchTechnologyTaxonomy,
    } = useTechnologiesDescriptionQuery();

    const intentIndex = useMemo(
        () => (intentTaxonomy ? indexIntentTaxonomy(intentTaxonomy) : undefined),
        [intentTaxonomy],
    );
    const technologyIndex = useMemo(
        () => (technologyTaxonomy ? indexTechnologyTaxonomy(technologyTaxonomy) : undefined),
        [technologyTaxonomy],
    );

    // Keyed on the taxonomy alone: search must not be rebuilt when the selection changes, or every
    // checkbox click during an active query rescans the whole tree.
    const intentSearch = useMemo(
        () => (intentIndex ? createSubstringTaxonomySearch(intentIndex, locale) : undefined),
        [intentIndex, locale],
    );
    const technologySearch = useMemo(
        () => (technologyIndex ? createSubstringTaxonomySearch(technologyIndex, locale) : undefined),
        [technologyIndex, locale],
    );

    const intentViewModel = useMemo(
        () =>
            intentIndex && intentSearch
                ? createSignalTreeSource({ taxonomy: intentIndex, value: intentSelectedIds, search: intentSearch })
                : undefined,
        [intentIndex, intentSearch, intentSelectedIds],
    );
    const technologyViewModel = useMemo(
        () =>
            technologyIndex && technologySearch
                ? createSignalTreeSource({
                      taxonomy: technologyIndex,
                      value: technologySelectedIds,
                      search: technologySearch,
                  })
                : undefined,
        [technologyIndex, technologySearch, technologySelectedIds],
    );

    return {
        isIntentFilterVisible,
        intent: {
            isPending: isIntentTaxonomyPending,
            isError: isIntentTaxonomyError,
            refetch: refetchIntentTaxonomy,
            viewModel: intentViewModel,
            selectedIds: intentSelectedIds,
            onChange: setIntentSelectedIds,
        },
        technology: {
            isPending: isTechnologyTaxonomyPending,
            isError: isTechnologyTaxonomyError,
            refetch: refetchTechnologyTaxonomy,
            viewModel: technologyViewModel,
            selectedIds: technologySelectedIds,
            onChange: setTechnologySelectedIds,
        },
        buildPayload: () => buildPayload({ intentIndex, technologyIndex }),
    };
};
